import React, { useCallback, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import {
  Typography,
  FormField,
  FormItem,
  FormMessage,
  CustomFrequencyDropdown,
  AssetRateDisplay,
  AssetSelector,
  StackAmountInput,
  FormSection,
  SwapButton,
  DateTimeInput,
} from "./ui";
import StackFormLayout from "./ui/stack-form-layout";
import StackFormButtons from "./ui/stack-form-buttons";
import {
  AutoStackFormData,
  AutoStackStepType as StepType,
} from "../pages/Autostack";
import { useAssets } from "../hooks/useAssets";
import {
  calculateDayStartDate,
  calculateWeekStartDate,
  calculateMonthStartDate,
} from "../utils/dateTime";
import { TIME_SLOTS } from "../constants/autostack";
import noteWarning from "../assets/icons/note_warning.svg";

interface AutoWalletStepProps {
  onStepComplete: () => void;
  onStepBack: () => void;
  onCancel: () => void;
  currentStep: StepType;
}

const AutoWalletStep: React.FC<AutoWalletStepProps> = React.memo(
  ({ onStepComplete, onStepBack, onCancel }) => {
    const form = useFormContext<AutoStackFormData>();
    const watchedAsset = form.watch("assetToBuy");
    const { selectedAsset, assets } = useAssets({
      selectedAssetValue: watchedAsset,
    });

    const handleSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault();
        onStepComplete();
      },
      [onStepComplete]
    );

    // Set default start date based on week calculation on initial load
    useEffect(() => {
      const currentStartDateTime = form.getValues("startDateTime");
      // Only set default if startDateTime is not already set
      if (!currentStartDateTime) {
        const defaultDateTime = calculateWeekStartDate("monday", TIME_SLOTS[0]);
        form.setValue("startDateTime", defaultDateTime);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update startDateTime when frequency changes
    const watchedFrequency = form.watch("frequency");
    const prevFrequencyRef = useRef<{
      type?: string;
      dayOfWeek?: string;
      dayOfMonth?: string;
      time?: string;
    }>({});

    useEffect(() => {
      if (!watchedFrequency?.type) return;

      const currentFrequency = {
        type: watchedFrequency.type,
        dayOfWeek: watchedFrequency.dayOfWeek,
        dayOfMonth: watchedFrequency.dayOfMonth,
        time: watchedFrequency.time,
      };

      const hasChanged =
        prevFrequencyRef.current.type !== currentFrequency.type ||
        prevFrequencyRef.current.dayOfWeek !== currentFrequency.dayOfWeek ||
        prevFrequencyRef.current.dayOfMonth !== currentFrequency.dayOfMonth ||
        prevFrequencyRef.current.time !== currentFrequency.time;

      if (hasChanged) {
        const defaultTime = TIME_SLOTS[0];
        let calculatedDateTime: string;

        switch (watchedFrequency.type) {
          case "day": {
            const selectedTime = watchedFrequency.time || defaultTime;
            calculatedDateTime = calculateDayStartDate(selectedTime);
            break;
          }
          case "week": {
            const dayOfWeek = watchedFrequency.dayOfWeek || "monday";
            calculatedDateTime = calculateWeekStartDate(dayOfWeek, defaultTime);
            break;
          }
          case "month": {
            const dayOfMonth = watchedFrequency.dayOfMonth || "1st";
            calculatedDateTime = calculateMonthStartDate(
              dayOfMonth,
              defaultTime
            );
            break;
          }
          default:
            calculatedDateTime = calculateWeekStartDate("monday", defaultTime);
        }

        form.setValue("startDateTime", calculatedDateTime);
        prevFrequencyRef.current = currentFrequency;
      }
    }, [
      watchedFrequency?.type,
      watchedFrequency?.dayOfWeek,
      watchedFrequency?.dayOfMonth,
      watchedFrequency?.time,
      form,
    ]);

    const renderAmountSection = useCallback(
      () => (
        <FormSection className="flex flex-col md:flex-row gap-4 md:gap-0 mb-4 min-h-24">
          <Typography variant="label">I want to stack</Typography>
          <StackAmountInput
            name="stackAmount"
            className="w-full md:w-1/3 flex"
            label="Stack amount"
          />
        </FormSection>
      ),
      []
    );

    const renderAssetSection = useCallback(
      () => (
        <FormSection className="flex flex-col md:flex-row gap-4 md:gap-0 mb-4 min-h-24">
          <div className="flex flex-col gap-4">
            <Typography variant="label">To buy</Typography>
            <AssetRateDisplay asset={selectedAsset} />
          </div>
          <AssetSelector
            name="assetToBuy"
            assets={assets}
            selectedAsset={selectedAsset}
            className="w-full md:w-1/3 flex"
            label="Asset to buy"
          />
        </FormSection>
      ),
      [selectedAsset, assets]
    );

    return (
      <StackFormLayout
        title="Create new AutoStack"
        onCancel={onCancel}
        showCancelHeader={true}
        maxWidth="max-w-3xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div
            className="relative"
            role="group"
            aria-label="Asset stacking configuration"
          >
            {renderAmountSection()}
            <SwapButton />
            {renderAssetSection()}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormSection>
              <FormField
                control={form.control}
                name="frequency"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <Typography variant="label">Every</Typography>
                    <CustomFrequencyDropdown
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select frequency"
                      className="w-full"
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            <FormSection>
              <div className="flex flex-col gap-3 w-full">
                <Typography variant="label">Start date & time</Typography>
                <DateTimeInput
                  name="startDateTime"
                  label="start date and time"
                  disablePastDates={true}
                />
              </div>
            </FormSection>
          </div>

          <div className="flex items-center gap-4 bg-orange-50 rounded-2xl py-3 px-20 text-orange-800">
            <img
              src={noteWarning}
              alt="warning"
              width={24}
              height={24}
              className="shrink-0"
            />
            <span className="text-sm font-semibold">
              The start date you select will determine when the first purchase
              of your chosen asset is executed. By continuing, you agree to
              begin on that date.
            </span>
          </div>

          <StackFormButtons onBack={onStepBack} isSubmitType={true} />
        </form>
      </StackFormLayout>
    );
  }
);

AutoWalletStep.displayName = "AutoWalletStep";

export default AutoWalletStep;
